import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { UserContext } from './App';
import { authFetch, fetchCsrfToken } from './authFetch';
import {
  Upload, FileSpreadsheet, AlertCircle, CheckCircle2, XCircle,
  Users, UserCheck, UserX, ShieldAlert, ChevronDown, Loader2
} from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const REFERRAL_SOURCE_OPTIONS = [
  { value: 'infofree_new_movers', label: 'Infofree - New Movers' },
  { value: 'infofree_aging_in', label: 'Infofree - Aging In' },
];

export default function AdminLeadImport() {
  const { currentUser } = useContext(UserContext) || { currentUser: null };

  // Admin check
  const isAdmin =
    !!(currentUser?.is_staff) ||
    (currentUser?.groups || []).some((g) => (g?.name || '').toLowerCase() === 'admin');

  // State
  const [file, setFile] = useState(null);
  const [referralSource, setReferralSource] = useState('infofree_new_movers');
  const [customReferralSource, setCustomReferralSource] = useState('');
  const [isCustomSource, setIsCustomSource] = useState(false);
  const [defaultAgentId, setDefaultAgentId] = useState('');
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  const [dryRun, setDryRun] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch agents on mount
  useEffect(() => {
    if (!isAdmin) return;
    setAgentsLoading(true);
    authFetch(`${BASE_URL}/api/agents/`)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setAgents(data);
        } else {
          console.error('Failed to fetch agents:', res.status);
        }
      })
      .catch((err) => console.error('Error fetching agents:', err))
      .finally(() => setAgentsLoading(false));
  }, [isAdmin]);

  // Derived values
  const effectiveReferralSource = isCustomSource ? customReferralSource.trim() : referralSource;
  const canDryRun = isAdmin && file && effectiveReferralSource && !processing;
  const canCommit = isAdmin && file && effectiveReferralSource && dryRun && !processing;

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && droppedFile.name.toLowerCase().endsWith('.csv')) {
      setFile(droppedFile);
      setDryRun(null);
      setCommitResult(null);
      setError('');
      setMessage('');
    } else {
      setError('Please drop a CSV file.');
    }
  }, []);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setDryRun(null);
    setCommitResult(null);
    setError('');
    setMessage('');
  };

  // Reset form
  const handleReset = () => {
    setFile(null);
    setDryRun(null);
    setCommitResult(null);
    setError('');
    setMessage('');
    setDefaultAgentId('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Dry run
  async function onDryRun() {
    if (!file || !effectiveReferralSource) return;
    setError('');
    setMessage('');
    setDryRun(null);
    setCommitResult(null);
    setProcessing(true);

    try {
      await fetchCsrfToken();

      const form = new FormData();
      form.append('file', file);
      form.append('referral_source', effectiveReferralSource);

      const res = await authFetch(`${BASE_URL}/api/imports/external-leads/dry-run/`, {
        method: 'POST',
        body: form,
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setDryRun(data);
      setMessage('Dry-run complete. Review the preview below, then click Import to commit.');
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setProcessing(false);
    }
  }

  // Commit
  async function onCommit() {
    if (!file || !effectiveReferralSource) return;
    setError('');
    setMessage('');
    setCommitResult(null);
    setProcessing(true);

    try {
      await fetchCsrfToken();

      const form = new FormData();
      form.append('file', file);
      form.append('referral_source', effectiveReferralSource);
      if (defaultAgentId) {
        form.append('default_agent_id', defaultAgentId);
      }

      const res = await authFetch(`${BASE_URL}/api/imports/external-leads/commit/`, {
        method: 'POST',
        body: form,
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setCommitResult(data);
      setDryRun(null);
      setMessage('Import completed successfully!');
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setProcessing(false);
    }
  }

  // Access denied for non-admin
  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="max-w-lg mx-auto bg-white border border-red-200 rounded-lg p-8 text-center shadow">
          <ShieldAlert className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-800">Access Denied</h1>
          <p className="mt-2 text-red-600">You don't have permission to access this page. Admin role required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-2xl font-semibold mb-4">Admin: External Lead Import</h1>

      {/* Guidelines */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
        <h3 className="font-semibold text-blue-800">Import Guidelines:</h3>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>- Upload a CSV file with external leads (e.g., from infofree.com)</li>
          <li>- Duplicate leads (matching by name + phone or name + address) will be skipped</li>
          <li>- Leads with an AssignedAgentName column will be matched to existing agents</li>
          <li>- Unmatched agents will use the Default Agent selected below</li>
          <li>- Always run a Dry Run first to preview what will be imported</li>
        </ul>
      </div>

      {/* Main form card */}
      <div className="border rounded-lg p-4 space-y-4 bg-white shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Referral Source */}
          <div>
            <label className="block text-sm font-medium mb-1">Referral Source</label>
            {!isCustomSource ? (
              <div className="space-y-2">
                <div className="relative">
                  <select
                    className="w-full border rounded px-3 py-2 appearance-none pr-8"
                    value={referralSource}
                    onChange={(e) => setReferralSource(e.target.value)}
                    disabled={processing}
                  >
                    {REFERRAL_SOURCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                <button
                  type="button"
                  onClick={() => setIsCustomSource(true)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                  disabled={processing}
                >
                  Use custom source
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter custom referral source..."
                  value={customReferralSource}
                  onChange={(e) => setCustomReferralSource(e.target.value)}
                  disabled={processing}
                />
                <button
                  type="button"
                  onClick={() => {
                    setIsCustomSource(false);
                    setCustomReferralSource('');
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                  disabled={processing}
                >
                  Use preset source
                </button>
              </div>
            )}
          </div>

          {/* Default Agent */}
          <div>
            <label className="block text-sm font-medium mb-1">Default Agent</label>
            <div className="relative">
              <select
                className="w-full border rounded px-3 py-2 appearance-none pr-8"
                value={defaultAgentId}
                onChange={(e) => setDefaultAgentId(e.target.value)}
                disabled={processing || agentsLoading}
              >
                <option value="">-- No default agent --</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.full_name || agent.username}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
            <p className="text-xs text-gray-500 mt-1">Used for leads without a matched agent</p>
          </div>

          {/* CSV File */}
          <div>
            <label className="block text-sm font-medium mb-1">CSV File</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="w-full border rounded px-3 py-2 text-sm"
              disabled={processing}
            />
          </div>
        </div>

        {/* Drag and Drop Zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !processing && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-blue-500 bg-blue-50'
              : file
                ? 'border-green-300 bg-green-50'
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }`}
        >
          {file ? (
            <div className="flex items-center justify-center gap-2 text-green-700">
              <FileSpreadsheet className="h-6 w-6" />
              <span className="font-medium">{file.name}</span>
              <span className="text-sm text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
            </div>
          ) : (
            <div className="text-gray-500">
              <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="font-medium">Drag and drop a CSV file here</p>
              <p className="text-sm">or click to browse</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 items-center">
          <button
            onClick={onDryRun}
            disabled={!canDryRun}
            className={`px-4 py-2 rounded text-white flex items-center gap-2 ${
              !canDryRun ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {processing && !dryRun && !commitResult ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4" />
            )}
            {processing && !dryRun && !commitResult ? 'Analyzing...' : 'Dry Run Preview'}
          </button>

          <button
            onClick={onCommit}
            disabled={!canCommit}
            className={`px-4 py-2 rounded text-white flex items-center gap-2 ${
              !canCommit ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
            title={!dryRun ? 'Run a dry-run first' : 'Import the leads'}
          >
            {processing && dryRun ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {processing && dryRun ? 'Importing...' : 'Import Leads'}
          </button>

          {(file || dryRun || commitResult) && (
            <button
              onClick={handleReset}
              disabled={processing}
              className="px-4 py-2 rounded text-gray-700 border border-gray-300 hover:bg-gray-100"
            >
              Reset
            </button>
          )}
        </div>

        {/* Messages */}
        {message && !commitResult && (
          <div className="text-sm text-green-700 bg-green-50 p-3 rounded border border-green-200 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{message}</span>
          </div>
        )}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="whitespace-pre-wrap">{error}</span>
          </div>
        )}
      </div>

      {/* Commit Results */}
      {commitResult && (
        <div className="mt-6 border rounded-lg p-4 bg-white shadow">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Import Results
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="bg-blue-50 p-3 rounded">
              <div className="font-semibold text-blue-800">Total Rows</div>
              <div className="text-xl text-blue-600">{commitResult.total_rows}</div>
            </div>
            <div className="bg-green-50 p-3 rounded">
              <div className="font-semibold text-green-800">Created</div>
              <div className="text-xl text-green-600">{commitResult.created}</div>
            </div>
            <div className="bg-yellow-50 p-3 rounded">
              <div className="font-semibold text-yellow-800">Skipped</div>
              <div className="text-xl text-yellow-600">{commitResult.skipped}</div>
            </div>
            <div className="bg-red-50 p-3 rounded">
              <div className="font-semibold text-red-800">Errors</div>
              <div className="text-xl text-red-600">{commitResult.errors?.length || 0}</div>
            </div>
          </div>

          {/* Error details */}
          {commitResult.errors && commitResult.errors.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-red-800 mb-2">Error Details:</h3>
              <div className="bg-red-50 border border-red-200 rounded p-3 max-h-48 overflow-y-auto">
                {commitResult.errors.map((err, idx) => (
                  <div key={idx} className="text-xs text-red-700 py-1 border-b border-red-100 last:border-0">
                    {typeof err === 'string' ? err : JSON.stringify(err)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dry Run Results */}
      {dryRun && !commitResult && (
        <div className="mt-6 space-y-4">
          {/* Summary Stats */}
          <div className="border rounded-lg p-4 bg-white shadow">
            <h2 className="text-lg font-semibold mb-3">Import Preview Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-blue-50 p-3 rounded">
                <div className="font-semibold text-blue-800">Total Rows</div>
                <div className="text-xl text-blue-600">{dryRun.total_rows}</div>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <div className="font-semibold text-green-800">New Leads</div>
                <div className="text-xl text-green-600">{dryRun.new_leads}</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <div className="font-semibold text-yellow-800">Duplicates</div>
                <div className="text-xl text-yellow-600">{dryRun.duplicates}</div>
              </div>
              <div className="bg-purple-50 p-3 rounded">
                <div className="font-semibold text-purple-800">Referral Source</div>
                <div className="text-sm text-purple-600 font-mono mt-1">{dryRun.referral_source}</div>
              </div>
            </div>
          </div>

          {/* Agent Matching */}
          <div className="border rounded-lg p-4 bg-white shadow">
            <h2 className="text-lg font-semibold mb-3">Agent Matching</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Matched Agents */}
              <div>
                <h3 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-1">
                  <UserCheck className="h-4 w-4" />
                  Matched Agents ({(dryRun.matched_agents || []).length})
                </h3>
                {(dryRun.matched_agents || []).length > 0 ? (
                  <div className="space-y-1">
                    {dryRun.matched_agents.map((agent, idx) => (
                      <div key={idx} className="text-sm bg-green-50 px-3 py-1.5 rounded border border-green-200">
                        {typeof agent === 'string' ? agent : agent.name || JSON.stringify(agent)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No agent names matched</p>
                )}
              </div>

              {/* Unmatched Agents */}
              <div>
                <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1">
                  <UserX className="h-4 w-4" />
                  Unmatched Agents ({(dryRun.unmatched_agents || []).length})
                </h3>
                {(dryRun.unmatched_agents || []).length > 0 ? (
                  <div className="space-y-1">
                    {dryRun.unmatched_agents.map((agent, idx) => (
                      <div key={idx} className="text-sm bg-amber-50 px-3 py-1.5 rounded border border-amber-200">
                        {typeof agent === 'string' ? agent : agent.name || JSON.stringify(agent)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">All agents matched</p>
                )}
                {(dryRun.unmatched_agents || []).length > 0 && !defaultAgentId && (
                  <div className="mt-2 text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200 flex items-start gap-1">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>Select a Default Agent above to assign these leads</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Columns Detected */}
          {dryRun.columns && dryRun.columns.length > 0 && (
            <div className="border rounded-lg p-4 bg-white shadow">
              <h3 className="text-lg font-semibold mb-3">Columns Detected ({dryRun.columns.length})</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                {dryRun.columns.map((column, index) => (
                  <div key={index} className="bg-gray-50 p-2 rounded border">
                    {column}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview Table */}
          {dryRun.preview && dryRun.preview.length > 0 && (
            <div className="border rounded-lg p-4 bg-white shadow">
              <h3 className="text-lg font-semibold mb-3">
                Lead Preview
                <span className="text-sm font-normal text-gray-500 ml-2">
                  (showing first {dryRun.preview.length} rows)
                </span>
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Row</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Phone</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Assigned Agent</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Agent Matched</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dryRun.preview.map((row, index) => (
                      <tr key={index} className={`hover:bg-gray-50 ${row.is_duplicate ? 'bg-yellow-50/50' : ''}`}>
                        <td className="px-3 py-2 text-gray-500">{row.row}</td>
                        <td className="px-3 py-2 font-medium">
                          {row.first_name} {row.last_name}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{row.phone || '--'}</td>
                        <td className="px-3 py-2">{row.assigned_agent || '--'}</td>
                        <td className="px-3 py-2">
                          {row.agent_matched ? (
                            <span className="inline-flex items-center gap-1 text-green-700">
                              <UserCheck className="h-3.5 w-3.5" />
                              Yes
                            </span>
                          ) : row.assigned_agent ? (
                            <span className="inline-flex items-center gap-1 text-amber-600">
                              <UserX className="h-3.5 w-3.5" />
                              No
                            </span>
                          ) : (
                            <span className="text-gray-400">--</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {row.is_duplicate ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800 border border-yellow-200">
                              <XCircle className="h-3 w-3" />
                              Duplicate
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800 border border-green-200">
                              <CheckCircle2 className="h-3 w-3" />
                              New
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
